/**
 * @engine/vfx — Slang type/literal/struct emission.
 *
 * The VFX codegen targets Slang (compiled to WGSL via the injected compile
 * service). This module maps VFX data types and JS values to Slang source, and
 * emits the generated module's structs/bindings from the canonical `Layouts`.
 */

import type { GPUTypeInfo, StructFieldDef } from '@engine/render2d';
import type { VFXDataType, ParticleAttributes } from '../../types';
import { DEFAULT_PARTICLE_ATTRIBUTES } from '../../types';
import { VFX_BINDINGS } from '../../VFXBindings';
import { UNIFORMS_LAYOUT, COUNTERS_LAYOUT, GPU_EVENT_LAYOUT, EVENT_COUNT_LAYOUT } from '../Layouts';

// ============================================================================
// TYPE MAPPING
// ============================================================================

/** Map VFX port types to Slang types. */
export const SLANG_TYPES: Record<VFXDataType, string> = {
  bool: 'bool',
  int: 'int',
  uint: 'uint',
  float: 'float',
  vec2: 'float2',
  vec3: 'float3',
  vec4: 'float4',
  color: 'float4',
  mat3: 'float3x3',
  mat4: 'float4x4',
  texture2d: 'Texture2D<float4>',
  texture3d: 'Texture3D<float4>',
  pointmap: 'Texture2D<float4>',
  gradient: '(gradient)', // callable port — never a parameter type
  curve: '(curve)',       // callable port — never a parameter type
  flow: 'void',
  particle: 'Particle',
  event: 'void',
  spawnEvent: 'void',
};

/** Format a number as a Slang float literal. */
export function formatFloat(n: number): string {
  if (!Number.isFinite(n)) return n > 0 ? '3.402823e+38' : (n < 0 ? '-3.402823e+38' : '0.0');
  const s = String(n);
  return s.includes('.') || s.includes('e') || s.includes('E') ? s : `${s}.0`;
}

/**
 * Convert a JS value to a Slang literal.
 * @param options - For enum-like int inputs: option labels (string values are
 *   resolved to their index).
 */
export function valueToSlang(value: unknown, type: VFXDataType, options?: readonly string[]): string {
  if (value === null || value === undefined) return defaultValueSlang(type);

  switch (type) {
    case 'bool':
      return value ? 'true' : 'false';
    case 'int': {
      if (typeof value === 'string' && options && options.length > 0) {
        const index = options.findIndex(opt => opt.toLowerCase() === value.toLowerCase());
        return String(index >= 0 ? index : 0);
      }
      const num = Number(value);
      return String(Number.isNaN(num) ? 0 : Math.round(num));
    }
    case 'uint': {
      const num = Number(value);
      return `${Number.isNaN(num) ? 0 : Math.max(0, Math.round(num))}u`;
    }
    case 'float':
      return formatFloat(Number(value));
    case 'vec2': {
      if (typeof value === 'object') {
        const v = value as { x?: number; y?: number };
        return `float2(${formatFloat(v.x ?? 0)}, ${formatFloat(v.y ?? 0)})`;
      }
      return `float2(${formatFloat(Number(value))})`;
    }
    case 'vec3': {
      if (typeof value === 'object') {
        const v = value as { x?: number; y?: number; z?: number };
        return `float3(${formatFloat(v.x ?? 0)}, ${formatFloat(v.y ?? 0)}, ${formatFloat(v.z ?? 0)})`;
      }
      return `float3(${formatFloat(Number(value))})`;
    }
    case 'vec4':
    case 'color': {
      if (typeof value === 'object') {
        const v = value as {
          x?: number; y?: number; z?: number; w?: number;
          r?: number; g?: number; b?: number; a?: number;
          intensity?: number;
        };
        // HDR intensity multiplier (bloom glow) applies to rgb only
        const intensity = v.intensity ?? 1.0;
        const r = (v.x ?? v.r ?? 0) * intensity;
        const g = (v.y ?? v.g ?? 0) * intensity;
        const b = (v.z ?? v.b ?? 0) * intensity;
        const a = v.w ?? v.a ?? 1;
        return `float4(${formatFloat(r)}, ${formatFloat(g)}, ${formatFloat(b)}, ${formatFloat(a)})`;
      }
      return `float4(${formatFloat(Number(value))})`;
    }
    default:
      return '0.0';
  }
}

/** Zero value for a Slang-typed port. */
export function defaultValueSlang(type: VFXDataType): string {
  switch (type) {
    case 'bool': return 'false';
    case 'int': return '0';
    case 'uint': return '0u';
    case 'float': return '0.0';
    case 'vec2': return 'float2(0.0)';
    case 'vec3': return 'float3(0.0)';
    case 'vec4':
    case 'color': return 'float4(0.0)';
    default: return '0.0';
  }
}

// ============================================================================
// STRUCT EMISSION (from Layouts / ParticleAttributes)
// ============================================================================

const SCALAR_TO_SLANG: Record<GPUTypeInfo['scalar'], string> = {
  f32: 'float',
  u32: 'uint',
  i32: 'int',
  f16: 'half',
  bool: 'bool',
};

/** Map a GPUTypeInfo (from defineStruct) to its Slang type. */
export function gpuTypeToSlang(t: GPUTypeInfo): string {
  const scalar = SCALAR_TO_SLANG[t.scalar];
  if (t.isAtomic) return `Atomic<${scalar}>`;
  if (t.isMatrix) {
    // components: 9 → 3x3, 16 → 4x4 (only f32 matrices exist in layouts)
    const dim = Math.round(Math.sqrt(t.components));
    return `${scalar}${dim}x${dim}`;
  }
  if (t.components === 1) return scalar;
  return `${scalar}${t.components}`;
}

/** Emit a Slang struct from a defineStruct layout. */
export function layoutToSlang(name: string, fields: readonly StructFieldDef[]): string {
  const lines = [`struct ${name}`, '{'];
  for (const f of fields) {
    lines.push(`    ${gpuTypeToSlang(f.type)} ${f.name};`);
  }
  lines.push('};');
  return lines.join('\n');
}

/**
 * Emit the Particle struct from the graph's attribute set.
 * Field order matters: the layout must stay byte-identical to the WGSL
 * Particle struct the render (vertex) shader declares — Slang std430 and WGSL
 * storage layout agree for these field types.
 */
export function particleStructSlang(attributes?: ParticleAttributes): string {
  const attrs = attributes ?? DEFAULT_PARTICLE_ATTRIBUTES;
  const lines = ['struct Particle', '{'];
  for (const [name, type] of Object.entries(attrs)) {
    if (!type) continue;
    lines.push(`    ${SLANG_TYPES[type as VFXDataType] ?? 'float'} ${name};`);
  }
  lines.push('    uint alive;');
  lines.push('};');
  return lines.join('\n');
}

// ============================================================================
// MODULE HEADER (structs + bindings)
// ============================================================================

export interface ComputeHeaderOptions {
  readonly attributes?: ParticleAttributes;
  /** Texture variable slots (0-7) referenced by the shader; only these are declared. */
  readonly textureSlots?: readonly number[];
  /**
   * Editor tweak mode: append a `float4 tweaks[N]` array to VFXVariables
   * (fixed capacity so init/update modules agree on the layout; the renderer
   * sizes the buffer to match — see codegen/Tweaks.ts).
   */
  readonly tweakCapacity?: number;
}

/**
 * VFXVariables uniform struct — packed exactly like the runtime writes it
 * (16 floats, 8 vec2s, 8 padded vec3s, 8 colors; see VFXSystemRenderer).
 */
function vfxVariablesStruct(tweakCapacity: number): string {
  const tweaks = tweakCapacity > 0
    ? `\n    float4 tweaks[${tweakCapacity}];  // editor live-preview tweak slots (see codegen/Tweaks.ts)`
    : '';
  return `struct VFXVariables
{
    float4 floats[4];  // 16 floats packed as 4 float4s
    float4 vec2s[4];   // 8 float2s packed as 4 float4s
    float4 vec3s[8];   // 8 float3s padded to float4
    float4 colors[8];  // 8 colors${tweaks}
};`;
}

/**
 * Emit the shared header of a generated compute module: imports, structs and
 * bindings. Counters/events are RWStructuredBuffer (arrays in WGSL — same
 * buffer bytes as v1's single-struct bindings); stage code indexes `[0]`.
 */
export function computeModuleHeader(opts: ComputeHeaderOptions = {}): string {
  const parts: string[] = [];

  parts.push('// Auto-generated VFX compute module. Do not edit.');
  parts.push('import vfx.common;');
  parts.push('import vfx.noise;');
  parts.push('import vfx.emit;');
  parts.push('');
  parts.push(particleStructSlang(opts.attributes));
  parts.push('');
  parts.push(layoutToSlang('Uniforms', UNIFORMS_LAYOUT.fields));
  parts.push('');
  parts.push(layoutToSlang('Counters', COUNTERS_LAYOUT.fields));
  parts.push('');
  parts.push(layoutToSlang('GPUEvent', GPU_EVENT_LAYOUT.fields));
  parts.push('');
  parts.push(layoutToSlang('EventCount', EVENT_COUNT_LAYOUT.fields));
  parts.push('');
  parts.push(vfxVariablesStruct(opts.tweakCapacity ?? 0));
  parts.push('');
  parts.push(`static const uint MAX_EVENTS = 1024u;`);
  parts.push('');
  parts.push(`// VFX standard bindings (see VFXBindings.ts)`);
  parts.push(`[vk::binding(${VFX_BINDINGS.UNIFORMS}, 0)] ConstantBuffer<Uniforms> uniforms;`);
  parts.push(`[vk::binding(${VFX_BINDINGS.PARTICLES}, 0)] RWStructuredBuffer<Particle> particles;`);
  parts.push(`[vk::binding(${VFX_BINDINGS.COUNTERS}, 0)] RWStructuredBuffer<Counters> counters;`);
  parts.push(`[vk::binding(${VFX_BINDINGS.EVENTS}, 0)] RWStructuredBuffer<GPUEvent> events;`);
  parts.push(`[vk::binding(${VFX_BINDINGS.VFX_VARIABLES}, 0)] ConstantBuffer<VFXVariables> vfxVariables;`);
  parts.push(`[vk::binding(${VFX_BINDINGS.EVENT_COUNT}, 0)] RWStructuredBuffer<EventCount> eventCount;`);
  parts.push(`[vk::binding(${VFX_BINDINGS.DEAD_LIST}, 0)] RWStructuredBuffer<uint> deadList;`);

  const slots = [...new Set(opts.textureSlots ?? [])].sort((a, b) => a - b);
  if (slots.length > 0) {
    parts.push('');
    parts.push('// Texture variable bindings (group 1; layout always has all 8 slots)');
    for (const slot of slots) {
      parts.push(`[vk::binding(${slot}, 1)] Texture2D<float4> vfxTexture${slot};`);
    }
  }

  return parts.join('\n');
}
